import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Brain, Users, TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toolLabels = {
  jd_generator: 'JD Generator',
  resume_screening: 'Resume Screening',
  interview_questions: 'Interview Questions',
  candidate_insights: 'Candidate Insights',
  role_analyzer: 'Role & Market Analyzer',
  company_intelligence: 'Company Intel',
};

const toolColors = {
  jd_generator: 'bg-violet-500',
  resume_screening: 'bg-sky-500',
  interview_questions: 'bg-amber-500',
  candidate_insights: 'bg-emerald-500',
  role_analyzer: 'bg-rose-500',
  company_intelligence: 'bg-indigo-500',
};

export default function AIUsageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/ai-usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load AI usage data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={32} className="animate-spin text-blue-400" /></div>;

  const maxToolCount = Math.max(...(data?.by_tool || []).map(t => t.count), 1);

  return (
    <div data-testid="ai-usage-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <BarChart3 className="inline-block mr-2 mb-1" size={32} />AI Tools Analytics
        </h1>
        <p className="text-slate-500 text-sm">Track how your team uses RecruiterIQ AI tools</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-blue-100 rounded-lg p-4 text-center shadow-sm">
          <Brain size={24} className="mx-auto mb-2 text-blue-600" />
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900" data-testid="total-uses">{data?.total_uses || 0}</p>
          <p className="text-xs font-bold text-slate-500 uppercase">Total AI Uses</p>
        </div>
        <div className="bg-white border border-blue-100 rounded-lg p-4 text-center shadow-sm">
          <TrendingUp size={24} className="mx-auto mb-2 text-emerald-600" />
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900" data-testid="top-tool">{data?.by_tool?.[0] ? toolLabels[data.by_tool[0].tool] || data.by_tool[0].tool : '-'}</p>
          <p className="text-xs font-bold text-slate-500 uppercase">Most Used Tool</p>
        </div>
        <div className="bg-white border border-blue-100 rounded-lg p-4 text-center shadow-sm">
          <Users size={24} className="mx-auto mb-2 text-indigo-600" />
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900" data-testid="active-users">{data?.by_user?.length || 0}</p>
          <p className="text-xs font-bold text-slate-500 uppercase">Active Users</p>
        </div>
      </div>

      {/* Tool Usage Chart */}
      <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-5 mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-4">Usage by Tool</p>
        {(data?.by_tool || []).length === 0 ? (
          <p className="text-center text-slate-400 py-6">No AI usage data yet. Team members need to use RecruiterIQ tools first.</p>
        ) : (
          <div className="space-y-3">
            {(data.by_tool || []).map((t) => (
              <div key={t.tool} data-testid={`tool-bar-${t.tool}`} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-blue-900 w-36 truncate">{toolLabels[t.tool] || t.tool}</span>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${toolColors[t.tool] || 'bg-blue-500'} rounded-full transition-all flex items-center justify-end pr-2`} style={{ width: `${Math.max(8, (t.count / maxToolCount) * 100)}%` }}>
                    <span className="text-[10px] font-bold text-white">{t.count}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-500 w-12 text-right">{t.pct}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage by User */}
      <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-4">Usage by Team Member</p>
        {(data?.by_user || []).length === 0 ? (
          <p className="text-center text-slate-400 py-6">No user activity yet</p>
        ) : (
          <div className="space-y-3">
            {(data.by_user || []).map((u) => (
              <div key={u.name} data-testid={`user-row-${u.name}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">{u.name?.charAt(0)}</div>
                    <span className="text-sm font-bold text-blue-900">{u.name}</span>
                  </div>
                  <span className="text-sm font-extrabold text-blue-700">{u.total} uses</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(u.tools || {}).map(([tool, count]) => (
                    <span key={tool} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-semibold text-slate-600">
                      {toolLabels[tool] || tool}: <span className="text-blue-700">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
